import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TextField,
} from '@mui/material';

import { Helmet } from 'react-helmet-async';

import { UseFormRegister } from 'react-hook-form';
import { useUserContext } from '../../../../contexts/UserContext';

import FormStyle from '../FormStyle';
import showTableData from '../Functions/showTableData';
import api from '../../../../services/api';
import ChangedWithSuccess from './ChangedWithSuccess';

interface UpdateUser {
  id: number;
  name: string;
  surname: string;
  nameKana: string;
  surnameKana: string;
  postCode: string;
  stateCity: string;
  streetBanNumber: number;
  buildingNumber: string;
  phone: string;
  email: string;
}

function ChangeForm(
  {
    addFormValues,
    handleSubmit,
    setIsEditing,
    errors,
    getUser,
  }: {
    addFormValues: UseFormRegister<UpdateUser>,
    handleSubmit: any,
    setIsEditing: (isEditing: boolean) => void,
    errors: any,
    getUser: () => Promise<void>,
  },
) {
  // variables
  const { user, updateUserContext } = useUserContext();
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // functions
  const handleEditConfirmation = handleSubmit(async (data: UpdateUser) => {
    setIsLoading(true);
    api.put(`/v1/users/${user.id}`, data)
      .then(async (response) => {
        updateUserContext(response.data.data);
        await getUser();
        setHasChanged(true);
      }).catch((error) => {
        setErrorMessage(error.response.data.error);
      }).finally(() => {
        setIsLoading(false);
      });
  });

  // render conditions
  if (hasChanged) {
    return (
      <ChangedWithSuccess
        setIsEditing={setIsEditing}
        setHasChanged={setHasChanged}
      />
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="90vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="main">
      <Helmet>
        <title>プロフィール変更</title>
      </Helmet>
      <main
        style={{
          overflowY: 'auto',
          height: 'calc(100vh - 65px)',
        }}
      >
        <Box className="container-fluid my-1">
          <Box
            sx={FormStyle.previewHeader}
          >
            <h1 className="text-bold">プロフィール変更</h1>
            変更したいプロフィールをご入力いただき、「保存する」ボタンを押してください。
          </Box>
          <Box
            className="text-danger"
            sx={FormStyle.errorMessage}
          >
            <h4 className="text-bold">{errorMessage}</h4>
          </Box>
          <Box className="card">
            <Box className="card-body">
              <Grid
                container
                justifyContent="center"
                spacing={1}
              >
                <Grid item xs={12}>
                  <Table className="table-form-setting">
                    <TableBody>
                      {showTableData({
                        ticket: 'お名前',
                        firstValueLabel: '姓',
                        value: (
                          <TextField
                            {...addFormValues('surname')}
                            size="small"
                            helperText={errors.surname?.message}
                            error={!!errors.surname}
                            sx={{ width: '100%' }}
                            placeholder="山田"
                          />
                        ),
                        secondValueLabel: '名',
                        secondValue: (
                          <TextField
                            {...addFormValues('name')}
                            size="small"
                            helperText={errors.name?.message}
                            error={!!errors.name}
                            sx={{ width: '100%' }}
                            placeholder="太郎"
                          />
                        ),
                        isRequired: true,
                      })}
                      {showTableData({
                        ticket: 'お名前(フリガナ)',
                        firstValueLabel: 'セイ',
                        value: (
                          <TextField
                            {...addFormValues('surnameKana')}
                            size="small"
                            helperText={errors.surnameKana?.message}
                            error={!!errors.surnameKana}
                            sx={{ width: '100%' }}
                            placeholder="ヤマダ"
                          />
                        ),
                        secondValueLabel: 'メイ',
                        secondValue: (
                          <TextField
                            {...addFormValues('nameKana')}
                            size="small"
                            helperText={errors.nameKana?.message}
                            error={!!errors.nameKana}
                            sx={{ width: '100%' }}
                            placeholder="タロウ"
                          />
                        ),
                      })}
                      {showTableData({
                        ticket: '郵便番号',
                        value: (
                          <TextField
                            {...addFormValues('postCode')}
                            size="small"
                            helperText={errors.postCode?.message}
                            error={!!errors.postCode}
                            sx={{ width: '100%' }}
                            placeholder="1230001"
                          />
                        ),
                        extraText: '”－” (ハイフン)を入れずにご入力ください。',
                      })}
                      {showTableData({
                        ticket: '都道府県・市区町村',
                        value: (
                          <TextField
                            {...addFormValues('stateCity')}
                            size="small"
                            helperText={errors.stateCity?.message}
                            error={!!errors.stateCity}
                            sx={{ width: '100%' }}
                            placeholder="〇〇県〇〇市〇〇区△△△"
                          />
                        ),
                      })}
                      {showTableData({
                        ticket: '丁目・番地',
                        value: (
                          <TextField
                            {...addFormValues('streetBanNumber')}
                            size="small"
                            helperText={errors.streetBanNumber?.message}
                            error={!!errors.streetBanNumber}
                            sx={{ width: '100%' }}
                            placeholder="１－２－３"
                          />
                        ),
                      })}
                      {showTableData({
                        ticket: '建物名・部屋番号',
                        value: (
                          <TextField
                            {...addFormValues('buildingNumber')}
                            size="small"
                            helperText={errors.buildingNumber?.message}
                            error={!!errors.buildingNumber}
                            sx={{ width: '100%' }}
                            placeholder="建物名・部屋番号がある場合はご入力ください"
                          />
                        ),
                      })}
                      {showTableData({
                        ticket: '電話番号',
                        value: (
                          <TextField
                            {...addFormValues('phone')}
                            size="small"
                            helperText={errors.phone?.message}
                            error={!!errors.phone}
                            sx={{ width: '100%' }}
                            placeholder="03012345678"
                          />
                        ),
                        extraText: '”－” (ハイフン)を入れずにご入力ください。',
                      })}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid item>
                  <Button
                    onClick={() => { setIsEditing(false); }}
                    variant="outlined"
                    className="btn-outline-secondary"
                  >
                    戻る
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    onClick={handleEditConfirmation}
                    className="btn-primary"
                  >
                    保存する
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </main>
    </div>
  );
}

export default ChangeForm;
